import apiClient from "../common";
import type { SimulationBootstrapPayload } from "./createSimulation";
import type { SimulatedPurchaseOrderRef } from "./simulationsList";
import type { SimulationDeviationNotesState } from "views/admin/simulator/types/simulationDeviationNotes";

export type SavedSimulationDetail = {
  id: string;
  simulation_name: string;
  status: string;
  simulated_purchase_orders: SimulatedPurchaseOrderRef[];
};

/** Error de carga con mensaje apto para toast / modal. */
export class SimulationLoadError extends Error {
  readonly userTitle: string;
  readonly statusCode?: number;

  constructor(message: string, userTitle: string, statusCode?: number) {
    super(message);
    this.name = "SimulationLoadError";
    this.userTitle = userTitle;
    this.statusCode = statusCode;
  }
}

/**
 * GET de desviaciones habilitado por defecto al existir el endpoint.
 * Desactivar con `REACT_APP_SIMULATION_DEVIATIONS_LIST_API=false`.
 */
export const SIMULATION_DEVIATIONS_LIST_API_AVAILABLE =
  process.env.REACT_APP_SIMULATION_DEVIATIONS_LIST_API !== "false";

function readJsonApiAttr(
  attrs: Record<string, unknown>,
  snake: string,
  kebab: string,
): unknown {
  return attrs[snake] ?? attrs[kebab];
}

function parsePurchaseOrders(raw: unknown): SimulatedPurchaseOrderRef[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((item) => {
    const o = item as Record<string, unknown>;
    return {
      id: String(o.id ?? ""),
      document_number: String(
        o.document_number ?? o["document-number"] ?? "",
      ),
    };
  });
}

function normalizeSimulationDetail(payload: unknown): SavedSimulationDetail {
  if (payload == null || typeof payload !== "object") {
    throw new SimulationLoadError(
      "La respuesta del servidor no tiene el formato esperado.",
      "Simulación no disponible",
    );
  }
  const body = payload as { data?: Record<string, unknown> };
  const row = body.data ?? (payload as Record<string, unknown>);
  const attrs = (row.attributes as Record<string, unknown> | undefined) ?? row;
  const id = String(row.id ?? attrs.id ?? "").trim();
  if (!id) {
    throw new SimulationLoadError(
      "No pudimos identificar la simulación seleccionada.",
      "Simulación no disponible",
    );
  }
  return {
    id,
    simulation_name: String(attrs.simulation_name ?? ""),
    status: String(attrs.status ?? ""),
    simulated_purchase_orders: parsePurchaseOrders(
      readJsonApiAttr(
        attrs,
        "simulated_purchase_orders",
        "simulated-purchase-orders",
      ),
    ),
  };
}

/** GET /api/v1/simulations/{id}/ — valida existencia y metadatos antes de abrir sesión. */
export async function apiGetSimulationDetail(
  simulationId: string,
): Promise<SavedSimulationDetail> {
  const res = await apiClient.get(`simulations/${simulationId}/`);
  return normalizeSimulationDetail(res.data);
}

/** Deriva clave de período mensual (`YYYY-MM`) desde fecha ISO del backend. */
function periodKeyFromIsoDate(iso: string): string | null {
  const trimmed = iso.trim();
  const match = /^(\d{4})-(\d{2})/.exec(trimmed);
  if (!match) {
    return null;
  }
  return `${match[1]}-${match[2]}`;
}

function parseJsonApiDeviationRows(payload: unknown): Record<string, unknown>[] {
  if (payload == null) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload as Record<string, unknown>[];
  }
  const body = payload as { data?: unknown };
  if (Array.isArray(body.data)) {
    return body.data as Record<string, unknown>[];
  }
  if (body.data && typeof body.data === "object") {
    return [body.data as Record<string, unknown>];
  }
  return [];
}

/**
 * Hidrata `byProductPeriod` cuando el GET de desviaciones esté disponible.
 * Requiere cruce con `simulation_result_product_id` / `product_id` del payload.
 */
export async function tryFetchSimulationDeviationNotesHydration(
  simulationId: string,
  productIdByErpCode: Map<string, number>,
): Promise<Partial<SimulationDeviationNotesState> | null> {
  if (!SIMULATION_DEVIATIONS_LIST_API_AVAILABLE) {
    return null;
  }
  try {
    const res = await apiClient.get(`simulations/${simulationId}/deviations/`);
    const rows = parseJsonApiDeviationRows(res.data);
    const byProductPeriod: SimulationDeviationNotesState["byProductPeriod"] = {};

    for (const row of rows) {
      const attrs =
        (row.attributes as Record<string, unknown> | undefined) ?? row;
      const isActual = attrs.is_actual ?? attrs["is-actual"];
      if (isActual === true) {
        continue;
      }
      const erpCode = String(attrs.product_id ?? "").trim();
      const deviationNoteIdRaw =
        attrs.deviation_note_id ?? attrs["deviation-note-id"];
      const deviationNoteId =
        deviationNoteIdRaw != null ? String(deviationNoteIdRaw) : null;
      const rel = row.relationships as
        | { simulation_result_product?: { data?: { id?: unknown } } }
        | undefined;
      const srpFromRel = rel?.simulation_result_product?.data?.id;
      const srpIdRaw =
        attrs.simulation_result_product_id ??
        attrs["simulation-result-product-id"];
      const srpId =
        srpIdRaw != null
          ? String(srpIdRaw)
          : srpFromRel != null
            ? String(srpFromRel)
            : undefined;
      const productId = productIdByErpCode.get(erpCode);
      if (!productId || !deviationNoteId) {
        continue;
      }
      const periodStartRaw =
        attrs.period_start_date ??
        attrs["period-start-date"] ??
        attrs.detected_at ??
        undefined;
      const periodKeyRaw = attrs.period_key ?? attrs["period-key"];
      const periodKey =
        periodKeyRaw != null
          ? String(periodKeyRaw)
          : periodStartRaw != null
            ? periodKeyFromIsoDate(String(periodStartRaw)) ?? "unknown"
            : "unknown";
      const periodIndex = Number(attrs.period_index ?? 0);
      const compositeKey = `${productId}|${periodIndex}`;
      byProductPeriod[compositeKey] = {
        productId,
        erpCode,
        periodKey,
        periodStartDate:
          periodStartRaw != null ? String(periodStartRaw) : undefined,
        periodIndex: Number(attrs.period_index ?? 0),
        deviationNoteId,
        comments:
          attrs.comments != null ? String(attrs.comments) : undefined,
        simulationResultProductId: srpId,
      };
    }

    return Object.keys(byProductPeriod).length > 0
      ? { byProductPeriod }
      : null;
  } catch {
    return null;
  }
}

export function mapHttpErrorToSimulationLoadError(err: unknown): SimulationLoadError {
  if (err instanceof SimulationLoadError) {
    return err;
  }
  const response = (err as { response?: { status?: number; data?: unknown } })
    ?.response;
  const status = response?.status;

  if (status === 404) {
    return new SimulationLoadError(
      "Es posible que haya sido eliminada o que no tengas permiso para verla.",
      "No encontramos esa simulación",
      404,
    );
  }
  if (status === 403) {
    return new SimulationLoadError(
      "Tu usuario no tiene permiso para abrir simulaciones guardadas.",
      "Acceso denegado",
      403,
    );
  }
  if (status === 409) {
    return new SimulationLoadError(
      "La simulación está en un estado que no permite abrirla ahora.",
      "No se puede abrir",
      409,
    );
  }
  if (status === 422 || status === 400) {
    const detail = extractApiDetail(response?.data);
    return new SimulationLoadError(
      detail ??
        "Los datos de la simulación no son válidos para mostrarse en el mapa.",
      "Datos incompletos",
      status,
    );
  }
  if (!response) {
    return new SimulationLoadError(
      "Revisá tu conexión e intentá de nuevo.",
      "Sin conexión con el servidor",
    );
  }
  if (status != null && status >= 500) {
    return new SimulationLoadError(
      "El servidor respondió con un error. Probá de nuevo en unos minutos.",
      "Error al cargar la simulación",
      status,
    );
  }
  return new SimulationLoadError(
    "No pudimos preparar la simulación seleccionada.",
    "Error al cargar",
    status,
  );
}

function extractApiDetail(data: unknown): string | undefined {
  if (data == null || typeof data !== "object") {
    return undefined;
  }
  const errors = (data as { errors?: unknown[] }).errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors[0] as { detail?: string; title?: string };
    return first.detail ?? first.title;
  }
  const detail = (data as { detail?: string }).detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  return undefined;
}

/**
 * Prepara bootstrap para reabrir histórico: no recalcula en front;
 * la cobertura se obtiene vía GET v2 `coverage/list/{simulationId}/` (datos materializados en BD).
 */
export async function loadSavedSimulationBootstrap(
  simulationId: string,
): Promise<SimulationBootstrapPayload> {
  try {
    const detail = await apiGetSimulationDetail(simulationId);
    return {
      simulationId: detail.id,
      simulationName: detail.simulation_name || undefined,
      simulatedPurchaseOrders: detail.simulated_purchase_orders,
    };
  } catch (err) {
    throw mapHttpErrorToSimulationLoadError(err);
  }
}
